// src/viqai/ViqCards.jsx
// ─── Cartes de résultat VIQ.AI (§13) ────────────────────────────────────────
// Une réponse de VIQ.AI n'est pas du Markdown : chaque outil renvoie un type de
// carte déclaré dans `ai/schemas/cards.js`, rendu ici. Un type inconnu retombe
// sur la carte de repli — l'agent peut évoluer sans casser l'affichage.
//
// Aucune carte n'affiche de cote, d'argus ni de prix estimé : le produit ne les
// produit plus. La marge et les leviers de négociation prennent leur place.

import { useState } from "react";
import { buildLeBonCoinUrl } from "./api";

const eur = (n) => (n == null || isNaN(Number(n)) ? "—" : `${Number(n).toLocaleString("fr-FR")} €`);
const km = (n) => (n == null ? "—" : `${Number(n).toLocaleString("fr-FR")} km`);

function Card({ title, sub, children, tone }) {
  return (
    <div className={`viq-card${tone ? ` ${tone}` : ""}`}>
      {(title || sub) && (
        <div className="viq-card-head">
          {title && <div className="viq-card-title">{title}</div>}
          {sub && <div className="viq-card-sub">{sub}</div>}
        </div>
      )}
      {children}
    </div>
  );
}

function Row({ k, v, tone, total }) {
  return (
    <div className={`viq-row${total ? " total" : ""}`}>
      <span className="k">{k}</span>
      <span className={`v${tone ? ` ${tone}` : ""}`}>{v}</span>
    </div>
  );
}

function Actions({ items }) {
  const list = (items || []).filter(Boolean);
  if (!list.length) return null;
  return (
    <div className="viq-actions">
      {list.map((a, i) => (
        <button key={i} type="button" className={`viq-btn${a.primary ? " primary" : ""}`} onClick={a.onClick}>
          {a.label}
        </button>
      ))}
    </div>
  );
}

// ── Véhicule identifié ──────────────────────────────────────────────────────
function VehicleCard({ data, onAsk }) {
  if (data.found === false) {
    return <Card title="Véhicule non identifié" sub={data.identifier}>
      <div className="viq-empty">{data.message}</div>
    </Card>;
  }
  const label = [data.make, data.model, data.year].filter(Boolean).join(" ");
  return (
    <Card title={label} sub={data.plaque || data.vin}>
      <div className="viq-chips">
        {data.version && <span className="viq-chip">{data.version}</span>}
        {data.fuel && <span className="viq-chip">{data.fuel}</span>}
        {data.gearbox && <span className="viq-chip">{data.gearbox}</span>}
        {data.power_cv && <span className="viq-chip">{data.power_cv} CV</span>}
        {data.km != null && <span className="viq-chip">{km(data.km)}</span>}
      </div>
      <Actions items={[
        { label: "Créer le dossier", primary: true, onClick: () => onAsk(`Crée un dossier pour ${data.plaque || data.vin}.`) },
        { label: "Analyser la marge", onClick: () => onAsk("Analyse la marge possible sur ce véhicule.") },
        { label: "Voir le marché", onClick: () => onAsk("Cherche les annonces comparables de ce véhicule.") },
      ]} />
    </Card>
  );
}

// ── Dossier VIQ ─────────────────────────────────────────────────────────────
function DossierCard({ data, onAsk, onOpenDossier }) {
  return (
    <Card title={data.label || `Dossier #${data.id}`} sub={data.plaque || data.vin || null}>
      <div className="viq-rows">
        {data.km != null && <Row k="Kilométrage" v={km(data.km)} />}
        {data.score != null && <Row k="Score VIQ.AI®" v={`${data.score}/100`} tone={data.score >= 75 ? "pos" : data.score >= 55 ? "warn" : "neg"} />}
        {data.next_action && <Row k="Prochaine étape" v={data.next_action} />}
        {data.created && <Row k="Statut" v="Dossier créé" tone="pos" />}
      </div>
      {data.vigilance?.length > 0 && (
        <ul className="viq-bullets">
          {data.vigilance.slice(0, 3).map((v, i) => <li key={i}>{v}</li>)}
        </ul>
      )}
      <Actions items={[
        { label: "Ouvrir le dossier", primary: true, onClick: () => onOpenDossier(data.id) },
        !data.has_photos && { label: "Analyser des photos", onClick: () => onAsk("Analyse les photos de ce véhicule.") },
        { label: "Calculer la marge", onClick: () => onAsk("Quelle marge puis-je faire sur ce véhicule ?") },
      ]} />
    </Card>
  );
}

function DossierList({ data, onOpenDossier, onAsk }) {
  if (!data.count) {
    return <Card title="Aucun dossier">
      <div className="viq-empty">Rien ne correspond{data.query ? ` à « ${data.query} »` : ""}. Donnez-moi une plaque ou un VIN et j'ouvre un dossier.</div>
    </Card>;
  }
  return (
    <Card title={`${data.count} dossier${data.count > 1 ? "s" : ""}`} sub={data.filter && data.filter !== "all" ? data.filter : null}>
      <div className="viq-rows">
        {data.dossiers.map(d => (
          <div key={d.id} className="viq-row" style={{ cursor: "pointer" }} onClick={() => onOpenDossier(d.id)}>
            <span className="k">
              {d.label || [d.make, d.model, d.year].filter(Boolean).join(" ")}
              {d.plaque ? ` · ${d.plaque}` : ""}
              {d.jours_inactif != null ? ` · ${d.jours_inactif} j` : ""}
            </span>
            <span className="v">{d.marge_potentielle != null ? eur(d.marge_potentielle) : d.km != null ? km(d.km) : "—"}</span>
          </div>
        ))}
      </div>
      {data.stats && (
        <div className="viq-chips">
          <span className="viq-chip">{data.stats.totalDossiers ?? 0} au total</span>
          {data.stats.dormants > 0 && <span className="viq-chip warn">{data.stats.dormants} dormants</span>}
        </div>
      )}
      <Actions items={[{ label: "Que faut-il relancer ?", onClick: () => onAsk("Lesquels dois-je relancer en priorité et pourquoi ?") }]} />
    </Card>
  );
}

// ── Marge ───────────────────────────────────────────────────────────────────
// La carte centrale du produit. Elle ne montre AUCUNE valeur du véhicule :
// des charges, des leviers, une marge, et le prix de revente qui découle du
// prix d'achat annoncé par le professionnel.
function MarginCard({ data, onAsk, onOpenDossier }) {
  if (!data.available) {
    return (
      <Card title="Marge non calculable">
        <div className="viq-empty">{data.message}</div>
        {data.missing?.length > 0 && (
          <div className="viq-chips">{data.missing.map((m, i) => <span key={i} className="viq-chip warn">{m} manquant</span>)}</div>
        )}
        <Actions items={[{ label: "Compléter le dossier", primary: true, onClick: () => onOpenDossier(data.dossier_id) }]} />
      </Card>
    );
  }
  const c = data.charges || {};
  return (
    <Card title={data.label || "Marge de l'opération"} sub={data.km != null ? km(data.km) : null}>
      <div className="viq-rows">
        {data.prix_achat != null && <Row k="Prix d'achat annoncé" v={eur(data.prix_achat)} />}
        {c.preparation != null && <Row k="Préparation" v={eur(c.preparation)} />}
        {c.fonctionnement != null && <Row k="Frais de fonctionnement" v={eur(c.fonctionnement)} />}
        {c.acquisition != null && <Row k="Acquisition client" v={eur(c.acquisition)} />}
        {data.extra_costs ? <Row k="Frais ajoutés" v={eur(data.extra_costs)} /> : null}
        <Row k="Charges imputables" v={eur(data.charges_total)} />
        {data.levier_negociation_eur != null && <Row k="Levier de négociation" v={eur(data.levier_negociation_eur)} tone="pos" />}
        {data.marge_suggeree?.montant != null && <Row k="Marge brute suggérée" v={eur(data.marge_suggeree.montant)} total />}
        {data.prix_revente_cible != null && <Row k="Revente à viser" v={eur(data.prix_revente_cible)} />}
      </div>

      {data.axes_negociation?.length > 0 && (
        <>
          <div className="viq-card-sub" style={{ marginTop: ".9rem" }}>Ce que vous pouvez faire valoir</div>
          <ul className="viq-bullets">
            {data.axes_negociation.slice(0, 5).map((a, i) => (
              <li key={i}><strong>{a.titre}</strong>{a.levier_eur ? ` — ${eur(a.levier_eur)}` : ""} : {a.detail}</li>
            ))}
          </ul>
        </>
      )}
      <div className="viq-card-sub" style={{ marginTop: ".8rem" }}>{data.avertissement}</div>
      <Actions items={[
        { label: "Ouvrir le dossier", onClick: () => onOpenDossier(data.dossier_id) },
        { label: "Ajouter des frais", onClick: () => onAsk("Ajoute des frais à cette opération : ") },
      ]} />
    </Card>
  );
}

// ── Analyse photo (§13, §50) ────────────────────────────────────────────────
// Même richesse que le Dossier VIQ : identification, plaque détectée, zones,
// score visuel, points de vigilance. Une plaque non confirmée est affichée
// comme telle — jamais présentée comme acquise.
const ZONE_LABELS = {
  avant: "Avant", toit: "Toit et vitrages", flanc_gauche: "Flanc gauche",
  flanc_droit: "Flanc droit", arriere: "Arrière",
  jantes_pneus: "Jantes et pneus", interieur: "Intérieur",
};
const ZONE_TONE = { ok: "pos", attention: "warn", probleme: "neg" };
const ZONE_TEXT = { ok: "Rien à signaler", attention: "À surveiller", probleme: "Point dur", non_visible: "Non visible" };

function PhotoAnalysisCard({ data, onAsk, onOpenDossier }) {
  const defauts = data.vigilance_points || data.defauts || data.defects || [];
  const zones = data.zones || {};
  const visibleZones = Object.entries(zones).filter(([, v]) => v && v !== "non_visible");
  const label = [data.make, data.model, data.year].filter(Boolean).join(" ");

  return (
    <Card
      title={label || data.vehicle_guess?.make ? (label || `${data.vehicle_guess.make} ${data.vehicle_guess.model || ""}`.trim()) : "Analyse des photos"}
      sub={data.reused ? "Analyse déjà réalisée, réutilisée" : `${data.photo_count || 0} photo(s)`}>

      {/* Identification issue de la plaque lue sur les photos — le
          professionnel n'a rien saisi. */}
      {data.plate && (
        <div className="viq-rows">
          <Row k="Plaque détectée" v={data.plate}
            tone={data.plate_to_confirm ? "warn" : "pos"} />
          {data.plate_to_confirm && (
            <Row k="Fiabilité de la lecture" v={`${Math.round((data.plate_confidence || 0) * 100)} % — à confirmer`} tone="warn" />
          )}
        </div>
      )}
      {!data.plate && data.vehicle_guess?.make && (
        <div className="viq-card-sub" style={{ marginBottom: ".6rem" }}>
          Aucune plaque lisible sur les photos. Identification probable d'après la carrosserie,
          à confirmer avec une plaque ou un VIN.
        </div>
      )}

      {data.synthese && <div className="viq-answer" style={{ margin: ".7rem 0" }}>{data.synthese}</div>}
      {data.etat_general && !data.synthese && <div className="viq-answer" style={{ margin: ".7rem 0" }}>{data.etat_general}</div>}

      {visibleZones.length > 0 && (
        <div className="viq-rows">
          {visibleZones.map(([k, v]) => (
            <Row key={k} k={ZONE_LABELS[k] || k} v={ZONE_TEXT[v] || v} tone={ZONE_TONE[v]} />
          ))}
        </div>
      )}

      {data.score_delta != null && (
        <div className="viq-rows">
          <Row k="Impact sur le score" v={`${data.score_delta > 0 ? "+" : ""}${data.score_delta} pts`}
            tone={data.score_delta < 0 ? "neg" : "pos"} total />
        </div>
      )}

      {defauts.length > 0 && (
        <>
          <div className="viq-card-sub" style={{ marginTop: ".8rem" }}>Points à contrôler</div>
          <ul className="viq-bullets">
            {defauts.slice(0, 6).map((d, i) => (
              <li key={i}>{typeof d === "string" ? d : (d.titre || d.label)}</li>
            ))}
          </ul>
        </>
      )}

      {data.analysis_unavailable && (
        <div className="viq-card-sub" style={{ marginTop: ".7rem" }}>
          L'analyse visuelle détaillée n'a pas pu aboutir. La plaque lue reste exploitable.
        </div>
      )}
      {data.photos_saved && (
        <div className="viq-chips"><span className="viq-chip accent">Photos enregistrées au dossier</span></div>
      )}

      <Actions items={[
        !data.dossier_id && { label: "Créer le dossier", primary: true, onClick: () => onAsk(data.plate ? `Crée un dossier pour ${data.plate}.` : "Crée un dossier pour ce véhicule.") },
        data.dossier_id && { label: "Ouvrir le dossier", primary: true, onClick: () => onOpenDossier(data.dossier_id) },
        { label: "Chiffrer la remise en état", onClick: () => onAsk("Chiffre la remise en état à partir de ces points.") },
      ]} />
    </Card>
  );
}

// ── Fiabilité ───────────────────────────────────────────────────────────────
function KnownIssuesCard({ data }) {
  if (data.found === false) return <Card title="Fiabilité"><div className="viq-empty">{data.message}</div></Card>;
  return (
    <Card title={data.vehicle || "Fiabilité du modèle"} sub={data.reliability_label}>
      {data.summary && <div className="viq-answer" style={{ marginBottom: ".7rem" }}>{data.summary}</div>}
      {data.vigilance_top5?.length > 0 && (
        <ul className="viq-bullets">
          {data.vigilance_top5.map((v, i) => <li key={i}>{v}</li>)}
        </ul>
      )}
      {data.common_issues?.length > 0 && (
        <div className="viq-rows" style={{ marginTop: ".7rem" }}>
          {data.common_issues.slice(0, 4).map((it, i) => (
            <Row key={i} k={`${it.zone} — ${it.titre}`} v={it.cout_estime || it.km_typique || ""} tone={it.gravite === "majeur" ? "neg" : it.gravite === "modéré" ? "warn" : undefined} />
          ))}
        </div>
      )}
      {data.sources?.length > 0 && (
        <div className="viq-chips">
          {data.sources.slice(0, 3).map((s, i) => (
            <a key={i} className="viq-chip" href={s.url} target="_blank" rel="noopener noreferrer">{s.titre || "source"}</a>
          ))}
        </div>
      )}
    </Card>
  );
}

// ── Marché ──────────────────────────────────────────────────────────────────
function MarketCard({ data }) {
  if (data.found === false) return <Card title="Marché"><div className="viq-empty">{data.message}</div></Card>;
  return (
    <Card title={data.vehicle} sub={`Tension ${data.tension}`}>
      {data.synthese && <div className="viq-answer" style={{ marginBottom: ".6rem" }}>{data.synthese}</div>}
      <div className="viq-rows">
        {data.delai_vente_jours != null && <Row k="Délai de revente observé" v={`${data.delai_vente_jours} jours`} />}
        {data.profil_acheteur && <Row k="Acheteur type" v={data.profil_acheteur} />}
      </div>
      {data.freins_vente?.length > 0 && (
        <ul className="viq-bullets">{data.freins_vente.slice(0, 4).map((f, i) => <li key={i}>{f}</li>)}</ul>
      )}
    </Card>
  );
}

function MarketSearchCard({ data }) {
  const url = data.url || (data.criteria ? buildLeBonCoinUrl(data.criteria) : null);
  const platform = data.platform === "lacentrale" ? "La Centrale" : "LeBonCoin";
  return (
    <Card title={`Vérifier le marché — ${platform}`} sub={data.label}>
      {data.criteria && (
        <div className="viq-chips">
          {data.criteria.brand && <span className="viq-chip">{data.criteria.brand}</span>}
          {data.criteria.keywords && <span className="viq-chip">{data.criteria.keywords}</span>}
          {data.criteria.yearMin && <span className="viq-chip">{data.criteria.yearMin}–{data.criteria.yearMax}</span>}
          {data.criteria.fuel && <span className="viq-chip">{data.criteria.fuel}</span>}
          {data.criteria.kmMax && <span className="viq-chip">≤ {km(data.criteria.kmMax)}</span>}
        </div>
      )}
      {data.note && <div className="viq-card-sub" style={{ marginTop: ".7rem" }}>{data.note}</div>}
      <Actions items={[{ label: `Ouvrir ${platform}`, primary: true, onClick: () => url && window.open(url, "_blank", "noopener,noreferrer") }]} />
    </Card>
  );
}

// ── HistoVec / CarVertical ──────────────────────────────────────────────────
function HistovecCard({ data, onAsk }) {
  if (data.found === false) return <Card title="HistoVec"><div className="viq-empty">{data.message}</div></Card>;
  const flag = (v, bad) => (v === bad ? "neg" : v == null ? undefined : "pos");
  return (
    <Card title="Rapport HistoVec" sub={[data.marque, data.modele].filter(Boolean).join(" ") || null}>
      <div className="viq-rows">
        {data.nb_proprietaires != null && <Row k="Propriétaires" v={data.nb_proprietaires} />}
        {data.ct_valide != null && <Row k="Contrôle technique" v={data.ct_valide ? "En cours de validité" : "À refaire"} tone={flag(data.ct_valide, false)} />}
        {data.sinistres_declares != null && <Row k="Sinistres déclarés" v={data.sinistres_declares} tone={data.sinistres_declares > 0 ? "warn" : "pos"} />}
        {data.gage != null && <Row k="Gage" v={data.gage ? "Véhicule gagé" : "Aucun"} tone={flag(data.gage, true)} />}
        {data.opposition != null && <Row k="Opposition" v={data.opposition ? "Oui" : "Aucune"} tone={flag(data.opposition, true)} />}
        {data.km_dernier_connu != null && <Row k="Dernier kilométrage connu" v={km(data.km_dernier_connu)} />}
      </div>
      {data.resume && <div className="viq-card-sub" style={{ marginTop: ".7rem" }}>{data.resume}</div>}
      <Actions items={[{ label: "Rattacher au dossier", primary: true, onClick: () => onAsk("Rattache ce rapport HistoVec au dossier du véhicule.") }]} />
    </Card>
  );
}

function CarVerticalCard({ data }) {
  return (
    <Card title={data.title} sub={data.vin}>
      <div className="viq-card-sub">{data.note}</div>
      <Actions items={[{ label: "Obtenir le rapport", primary: true, onClick: () => window.open(data.url, "_blank", "noopener,noreferrer") }]} />
    </Card>
  );
}

// ── Annonce ─────────────────────────────────────────────────────────────────
function ListingCard({ data }) {
  const [copied, setCopied] = useState(null);
  const copy = (text, which) => {
    navigator.clipboard?.writeText(text).then(() => { setCopied(which); setTimeout(() => setCopied(null), 1800); });
  };
  return (
    <Card title="Annonce prête à publier" sub={data.recommandation?.plateforme}>
      <div style={{ fontWeight: 650, fontSize: ".92rem", marginBottom: ".5rem" }}>{data.titre}</div>
      <div className="viq-answer" style={{ maxHeight: 220, overflowY: "auto", fontSize: ".86rem" }}>{data.description}</div>
      {data.recommandation?.raison && <div className="viq-card-sub" style={{ marginTop: ".7rem" }}>{data.recommandation.raison}</div>}
      <Actions items={[
        { label: copied === "long" ? "Copié" : "Copier l'annonce", primary: true, onClick: () => copy(`${data.titre}\n\n${data.description}`, "long") },
        { label: copied === "court" ? "Copié" : "Version courte", onClick: () => copy(data.description_courte, "court") },
      ]} />
    </Card>
  );
}

// ── Facture ─────────────────────────────────────────────────────────────────
function InvoiceCard({ data, onAsk, onConfirmSave }) {
  if (data.saved) {
    return <Card title="Charge enregistrée" sub={data.category}>
      <div className="viq-rows">
        <Row k={data.label} v={eur(data.amount_ttc)} tone="pos" />
      </div>
    </Card>;
  }
  if (data.found === false) return <Card title="Facture"><div className="viq-empty">{data.message}</div></Card>;
  const c = (v) => (v == null ? null : v / 100);
  return (
    <Card title={data.supplier || "Facture lue"} sub={data.invoice_number || data.expense_date}>
      <div className="viq-rows">
        <Row k="Libellé" v={data.label} />
        <Row k="Catégorie" v={data.category} />
        {data.amount_ht_cents != null && <Row k="HT" v={eur(c(data.amount_ht_cents))} />}
        {data.vat_cents != null && <Row k="TVA" v={eur(c(data.vat_cents))} />}
        <Row k="TTC" v={eur(c(data.amount_ttc_cents))} total />
      </div>
      {data.vat_on_margin && <div className="viq-chips"><span className="viq-chip warn">TVA sur marge — non récupérable</span></div>}
      {data.warnings?.length > 0 && (
        <ul className="viq-bullets">{data.warnings.slice(0, 3).map((w, i) => <li key={i}>{w}</li>)}</ul>
      )}
      <Actions items={[
        { label: "Enregistrer la charge", primary: true, onClick: () => onConfirmSave && onConfirmSave(data) },
        { label: "Corriger", onClick: () => onAsk("Corrige la facture : ") },
      ]} />
    </Card>
  );
}

// ── Leads / notes / agenda ──────────────────────────────────────────────────
// §27-30, §36-37 — Chaque lead porte ses actions. Un professionnel qui voit
// « VW T-Roc · score 82 » veut appeler ou ouvrir le dossier, pas retaper une
// phrase pour que le modèle le fasse à sa place. Trois actions visibles au
// maximum : au-delà, la liste devient un tableau de boutons illisible.
const LEAD_STATUS = {
  nouveau: ["Nouveau", "warn"], en_cours: ["En cours", undefined],
  converti: ["Converti", "pos"], refuse: ["Refusé", "neg"],
};

function LeadRow({ l, onAction }) {
  const actions = [
    { label: "Afficher", type: "navigate", destination: "leads" },
    l.dossier_id
      ? { label: "Ouvrir le dossier", type: "open_dossier", id: l.dossier_id }
      : { label: "Créer le dossier", type: "prompt", prompt: `Crée un dossier à partir du lead #${l.id}.` },
    l.phone ? { label: "Appeler", type: "tel", phone: l.phone } : null,
  ].filter(Boolean).slice(0, 3);

  const [st, tone] = LEAD_STATUS[l.status] || [l.status, undefined];
  return (
    <div className="viq-lead-row">
      <div className="viq-lead-main">
        <div className="viq-lead-title">{l.label}</div>
        <div className="viq-lead-meta">
          {[l.client, l.score != null ? `Score ${l.score}/100` : null,
            l.km ? `${Number(l.km).toLocaleString("fr-FR")} km` : null,
            l.created_at ? new Date(l.created_at).toLocaleDateString("fr-FR") : null,
           ].filter(Boolean).join(" · ")}
        </div>
      </div>
      <div className="viq-lead-side">
        {st && <span className={`viq-chip${tone === "warn" ? " warn" : tone === "pos" ? " accent" : ""}`}>{st}</span>}
        <div className="viq-lead-actions">
          {actions.map((a, i) => (
            <button key={i} type="button" className="viq-btn" onClick={() => onAction && onAction(a)}>{a.label}</button>
          ))}
        </div>
      </div>
    </div>
  );
}

function LeadList({ data, onAsk, onAction }) {
  // §38-39 — Une carte « aucune demande » ne s'affiche JAMAIS quand une autre
  // carte de la même réponse contient des leads. Deux tools peuvent renvoyer
  // des listes différemment filtrées ; afficher « 9 leads » puis « aucun lead »
  // dans la même réponse détruit la confiance dans tout le reste.
  if (!data.count) {
    if (data.suppressed) return null;
    return <Card title="Leads"><div className="viq-empty">Aucune demande client en attente.</div></Card>;
  }
  return (
    <Card title={`${data.count} lead${data.count > 1 ? "s" : ""}`}>
      <div className="viq-lead-list">
        {data.leads.map(l => <LeadRow key={l.id} l={l} onAction={onAction} />)}
      </div>
      <Actions items={[
        { label: "Lesquels traiter en premier ?", onClick: () => onAsk("Lesquels de ces leads dois-je traiter en priorité et pourquoi ?") },
      ]} />
    </Card>
  );
}

function NoteList({ data }) {
  if (!data.count) return <Card title="Bloc-notes"><div className="viq-empty">{data.due_only ? "Aucune relance à faire." : "Aucune note pour l'instant."}</div></Card>;
  return (
    <Card title={data.due_only ? "Relances à faire" : `${data.count} note${data.count > 1 ? "s" : ""}`}>
      <div className="viq-rows">
        {data.notes.map(n => (
          <Row key={n.id} k={n.title} v={n.follow_up_at ? new Date(n.follow_up_at).toLocaleDateString("fr-FR") : n.action} tone={n.follow_up_at ? "warn" : undefined} />
        ))}
      </div>
    </Card>
  );
}

function AgendaList({ data }) {
  if (!data.count) return <Card title="Agenda"><div className="viq-empty">Rien de prévu sur cette période.</div></Card>;
  return (
    <Card title="Agenda" sub={{ today: "Aujourd'hui", week: "7 jours", month: "30 jours", overdue: "En retard" }[data.range]}>
      <div className="viq-rows">
        {data.items.map(i => (
          <Row key={i.id} k={i.title} v={new Date(i.event_date).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })} tone={i.important ? "warn" : undefined} />
        ))}
      </div>
    </Card>
  );
}

function SimpleSaved({ data, title }) {
  return <Card title={title} sub={data.event_date ? new Date(data.event_date).toLocaleString("fr-FR") : null}>
    <div className="viq-rows"><Row k={data.title || data.label} v="Enregistré" tone="pos" /></div>
  </Card>;
}

function AlertCard({ data }) {
  return (
    <Card title="À prévoir">
      <div className="viq-rows">
        {data.malus_eur != null && <Row k="Malus écologique" v={eur(data.malus_eur)} tone="warn" />}
        {data.carte_grise_eur != null && <Row k="Carte grise" v={eur(data.carte_grise_eur)} />}
        {data.total_eur != null && <Row k="Total" v={eur(data.total_eur)} total />}
      </div>
      {data.note && <div className="viq-card-sub" style={{ marginTop: ".6rem" }}>{data.note}</div>}
    </Card>
  );
}

function DownloadCard({ data }) {
  return (
    <Card title={data.label} sub={data.format.toUpperCase()}>
      <Actions items={[{ label: `Télécharger le ${data.format.toUpperCase()}`, primary: true, onClick: () => window.open(data.url, "_blank", "noopener,noreferrer") }]} />
    </Card>
  );
}

// ── Réseau B2B ──────────────────────────────────────────────────────────────
function NetworkList({ data, onAction, onAsk }) {
  if (!data.count) return <Card title="Réseau"><div className="viq-empty">Aucune opportunité diffusée pour le moment.</div></Card>;
  return (
    <Card title={`${data.count} opportunité${data.count > 1 ? "s" : ""} sur le réseau`}>
      <div className="viq-lead-list">
        {data.opportunities.map(o => (
          <div key={o.publication_id} className="viq-lead-row">
            <div className="viq-lead-main">
              <div className="viq-lead-title">{o.label}</div>
              <div className="viq-lead-meta">
                {[o.km ? `${Number(o.km).toLocaleString("fr-FR")} km` : null, o.fuel,
                  o.asking_price != null ? eur(o.asking_price) : null, o.owner,
                  o.offers_count ? `${o.offers_count} offre${o.offers_count > 1 ? "s" : ""}` : null].filter(Boolean).join(" · ")}
              </div>
            </div>
            <div className="viq-lead-actions">
              <button type="button" className="viq-btn"
                onClick={() => onAction && onAction({ type: "network", id: o.publication_id })}>Voir</button>
              {!o.is_mine && (
                <button type="button" className="viq-btn"
                  onClick={() => onAsk(`Fais une offre sur la publication #${o.publication_id} (${o.label}) : `)}>Faire une offre</button>
              )}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function NetworkOfferCard({ data, onAction }) {
  return (
    <Card title="Offre déposée" tone="confirm">
      <div className="viq-rows">
        <Row k="Montant" v={eur(data.amount_eur)} tone="pos" />
        {data.expires_at && <Row k="Valable jusqu'au" v={new Date(data.expires_at).toLocaleString("fr-FR")} />}
        {data.message && <Row k="Message" v={data.message} />}
      </div>
      <div className="viq-card-sub" style={{ marginTop: ".6rem" }}>
        Le propriétaire est notifié. Votre offre reste privée entre vous et lui.
      </div>
      <Actions items={[{ label: "Voir la publication", onClick: () => onAction && onAction({ type: "network", id: data.publication_id }) }]} />
    </Card>
  );
}

// ── Remise en état (§13-14) ─────────────────────────────────────────────────
function RepairsCard({ data, onAsk }) {
  if (data.found === false) return <Card title="Remise en état"><div className="viq-empty">{data.message}</div></Card>;
  const P = { obligatoire: "neg", recommandee: "warn", cosmetique: undefined };
  return (
    <Card title="Remise en état" sub={data.from_photo_analysis ? "D'après l'analyse photo de la conversation" : data.vehicle}>
      {data.synthese && <div className="viq-answer" style={{ marginBottom: ".6rem" }}>{data.synthese}</div>}
      <div className="viq-rows">
        {(data.items || []).map((it, i) => (
          <Row key={i} k={`${it.defaut} — ${it.intervention}`}
            v={it.cout_min != null && it.cout_max != null ? `${eur(it.cout_min)} – ${eur(it.cout_max)}` : "—"}
            tone={P[it.priorite]} />
        ))}
        {data.total_min != null && <Row k="Total estimé" v={`${eur(data.total_min)} – ${eur(data.total_max)}`} total />}
        {data.levier_negociation != null && <Row k="Levier de négociation réaliste" v={eur(data.levier_negociation)} tone="pos" />}
      </div>
      {data.a_verifier_physiquement?.length > 0 && (
        <>
          <div className="viq-card-sub" style={{ marginTop: ".7rem" }}>À vérifier physiquement</div>
          <ul className="viq-bullets">{data.a_verifier_physiquement.map((x, i) => <li key={i}>{x}</li>)}</ul>
        </>
      )}
    </Card>
  );
}

// ── Quota épuisé — l'incitation au forfait ──────────────────────────────────
// Volontairement sobre : pas d'alarme rouge, pas de « erreur ». Le
// professionnel vient de voir ce que l'agent sait faire ; le message se
// contente de lui dire comment continuer.
function QuotaCard({ data, onUpgrade }) {
  return (
    <Card title="Vos analyses offertes sont épuisées" tone="confirm">
      <div className="viq-answer" style={{ fontSize: ".9rem" }}>{data.message}</div>
      <ul className="viq-bullets">
        <li>Analyses de véhicules et de photos sans limite</li>
        <li>Intelligence marché et analyse documentaire</li>
        <li>Mémoire complète de vos dossiers</li>
      </ul>
      <Actions items={[
        { label: "Découvrir les forfaits VIQ.AI", primary: true, onClick: () => onUpgrade && onUpgrade() },
      ]} />
    </Card>
  );
}

// ── Confirmation d'action sensible (§14) ────────────────────────────────────
function ActionConfirmation({ card, onConfirm, onCancel, resolved }) {
  if (resolved) {
    return <Card title="Action confirmée" tone="confirm">
      <div className="viq-rows"><Row k={card.data.label} v="Exécutée" tone="pos" /></div>
    </Card>;
  }
  return (
    <Card title="VIQ.AI souhaite" tone="confirm">
      <div className="viq-answer" style={{ fontSize: ".9rem" }}>{card.data.label}</div>
      <Actions items={[
        { label: "Confirmer", primary: true, onClick: () => onConfirm(card) },
        { label: "Annuler", onClick: () => onCancel(card) },
      ]} />
    </Card>
  );
}

// ── Prochaines actions (§34, §35) ───────────────────────────────────────────
// Construites à partir de ce que la carte contient RÉELLEMENT, pas d'une liste
// fixe : proposer « importer HistoVec » alors qu'il vient de l'être fait
// apprend à l'utilisateur à ignorer ces boutons.
const NEXT_ACTIONS = {
  photo_analysis_card: (d) => [
    { label: "Chiffrer les travaux", ask: "Chiffre les travaux à prévoir à partir de ces défauts." },
    !d.dossier_id && { label: "Identifier le véhicule", ask: "Identifie ce véhicule, voici sa plaque : " },
    !d.dossier_id && { label: "Créer le dossier", ask: "Crée un dossier pour ce véhicule." },
  ],
  vehicle_card: () => [
    { label: "Analyser la fiabilité", ask: "Quels sont les points faibles connus de ce modèle ?" },
    { label: "Voir le marché", ask: "Cherche les annonces comparables de ce véhicule." },
  ],
  histovec_card: (d) => [
    { label: "Évaluer le risque", ask: "Quel risque représente ce véhicule au vu de son historique ?" },
    { label: "Préparer CarVertical", ask: "Prépare le rapport CarVertical de ce véhicule." },
    d.dossier_id && { label: "Calculer la marge", ask: "Calcule ma marge sur ce véhicule." },
  ],
  margin_card: (d) => [
    d.dossier_id && { label: "Comparer au marché", ask: "Compare ce véhicule aux annonces du marché." },
    { label: "Générer une annonce", ask: "Génère une annonce de vente pour ce véhicule." },
  ],
  known_issues_card: () => [
    { label: "Chiffrer la négociation", ask: "Chiffre la négociation à partir de ces points de vigilance." },
  ],
};

function NextActions({ card, onAsk }) {
  const build = NEXT_ACTIONS[card?.type];
  if (!build) return null;
  const items = (build(card.data || {}) || []).filter(Boolean).slice(0, 4);
  if (!items.length) return null;
  return (
    <div className="viq-next">
      {items.map((a, i) => (
        <button key={i} type="button" className="viq-suggestion" onClick={() => onAsk(a.ask)}>{a.label}</button>
      ))}
    </div>
  );
}

export { NextActions };

// ── Aiguillage ──────────────────────────────────────────────────────────────
export default function ViqCard({ card, onAsk, onAction, onOpenDossier, onOpenInterface, onConfirm, onCancel, onUpgrade, resolved }) {
  const d = card?.data || {};
  switch (card?.type) {
    case "vehicle_card":         return <VehicleCard data={d} onAsk={onAsk} />;
    case "dossier_card":         return <DossierCard data={d} onAsk={onAsk} onOpenDossier={onOpenDossier} />;
    case "dossier_list":         return <DossierList data={d} onOpenDossier={onOpenDossier} onAsk={onAsk} />;
    case "margin_card":          return <MarginCard data={d} onAsk={onAsk} onOpenDossier={onOpenDossier} />;
    case "photo_analysis_card":  return <PhotoAnalysisCard data={d} onAsk={onAsk} onOpenDossier={onOpenDossier} />;
    case "known_issues_card":    return <KnownIssuesCard data={d} />;
    case "market_card":          return <MarketCard data={d} />;
    case "market_search_card":   return <MarketSearchCard data={d} />;
    case "histovec_card":        return <HistovecCard data={d} onAsk={onAsk} />;
    case "carvertical_card":     return <CarVerticalCard data={d} />;
    case "listing_card":         return <ListingCard data={d} />;
    case "invoice_card":         return <InvoiceCard data={d} onAsk={onAsk} onConfirmSave={() => onAsk("Enregistre cette charge.")} />;
    case "lead_list":            return <LeadList data={d} onAsk={onAsk} onAction={onAction} />;
    case "note_list":            return <NoteList data={d} />;
    case "note_card":            return <SimpleSaved data={d} title="Note ajoutée" />;
    case "agenda_list":          return <AgendaList data={d} />;
    case "agenda_card":          return <SimpleSaved data={d} title="Ajouté à l'agenda" />;
    case "alert_card":           return <AlertCard data={d} />;
    case "download_card":        return <DownloadCard data={d} />;
    case "open_interface":
      // La modale existante s'ouvre, pré-remplie : on ne la remplace pas (§12).
      onOpenInterface && onOpenInterface(d);
      return null;
    case "network_list":         return <NetworkList data={d} onAction={onAction} onAsk={onAsk} />;
    case "network_offer_card":   return <NetworkOfferCard data={d} onAction={onAction} />;
    case "repairs_card":         return <RepairsCard data={d} onAsk={onAsk} />;
    case "quota_card":           return <QuotaCard data={d} onUpgrade={onUpgrade} />;
    case "action_confirmation":  return <ActionConfirmation card={card} onConfirm={onConfirm} onCancel={onCancel} resolved={resolved} />;
    default:
      return <Card title="Résultat"><div className="viq-empty">{JSON.stringify(d).slice(0, 300)}</div></Card>;
  }
}
